import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import {
  loadDataFromStorage,
  saveDataToStorage,
} from "@/services/persistent_storage";
import { useStore } from "react-redux";

const OfflineCacheManager = ({ children }) => {
  const dispatch = useDispatch();
  const store = useStore(); // Get direct access to the Redux store
  const [isLoading, setIsLoading] = useState(true);

  const isOnline = useSelector((state) => state.network.isOnline);
  const wasOffline = useSelector((state) => state.network.wasOffline);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeCache = async () => {
      try {
        const isElectron =
          window.IS_ELECTRON || window.electronAPI !== undefined;
        if (!isElectron) {
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }

        // Load data from file storage
        const cachedData = await loadDataFromStorage();

        if (cachedData && Object.keys(cachedData).length > 0) {
          Object.keys(cachedData).forEach((key) => {
            if (cachedData[key]) {
              dispatch({
                type: `${key}/loadCachedData`,
                payload: cachedData[key],
              });
            }
          });
        }
      } catch (error) {
        console.error("Failed to load cached data:", error);
        message.error(
          "Error loading cached data. Some features may be unavailable."
        );
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeCache();
  }, [dispatch, isOnline]);

  useEffect(() => {
    const isElectron = window.IS_ELECTRON || window.electronAPI !== undefined;
    if (!isElectron) return;

    // Save data when coming back online after being offline
    if (isOnline && wasOffline) {
      try {
        const currentState = store.getState();
        saveDataToStorage(currentState);
      } catch (error) {
        console.error("Error saving data after network reconnect:", error);
      }
    }

    const saveInterval = setInterval(() => {
      try {
        const currentState = store.getState();
        saveDataToStorage(currentState);
      } catch (error) {
        console.error("Error during periodic save:", error);
      }
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(saveInterval);
    };
  }, [isOnline, wasOffline, store]);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="text-primary dark:text-white mt-4">
            Loading application data...
          </p>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-500 dark:text-red-400">
            Error initializing application. Please restart.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default OfflineCacheManager;

OfflineCacheManager.propTypes = {
  children: PropTypes.node,
};
